/**
 * Utilities per la gestione sicura del calendario
 * Previene problemi di timezone e offset di mese
 */

export interface SafeDateInfo {
  date: Date;
  dayString: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  displayDay: number;
}

/**
 * Genera i giorni per il calendario mensile in modo sicuro
 */
export const generateCalendarDays = (year: number, month: number): SafeDateInfo[] => {
  console.log('📅 generateCalendarDays chiamata con:', { year, month });
  
  // Crea il primo giorno del mese selezionato
  const firstDayOfMonth = new Date(year, month, 1, 0, 0, 0, 0);
  const lastDayOfMonth = new Date(year, month + 1, 0, 0, 0, 0, 0);
  
  // Trova il lunedì della prima settimana che contiene il primo giorno del mese
  const firstDayWeekday = firstDayOfMonth.getDay(); // 0 = Domenica, 1 = Lunedì, ..., 6 = Sabato
  // Per iniziare da lunedì: se è domenica (0) sottraiamo 6 giorni, altrimenti sottraiamo (firstDayWeekday - 1)
  const daysToSubtract = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;

  const startDate = new Date(year, month, 1 - daysToSubtract, 0, 0, 0, 0);
  
  console.log('📅 Parametri calendario:', {
    firstDayOfMonth,
    lastDayOfMonth,
    firstDayWeekday,
    daysToSubtract,
    startDate
  });
  
  const days: SafeDateInfo[] = [];
  const currentDate = new Date(startDate);
  
  // Genera esattamente 42 giorni (6 settimane)
  for (let i = 0; i < 42; i++) {
    const dayYear = currentDate.getFullYear();
    const dayMonth = currentDate.getMonth() + 1;
    const dayDay = currentDate.getDate();
    
    const dayString = `${dayYear}-${String(dayMonth).padStart(2, '0')}-${String(dayDay).padStart(2, '0')}`;
    const isCurrentMonth = currentDate.getMonth() === month;
    
    // Controlla se è oggi usando date locali
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isToday = dayString === todayString;
    
    // Crea una copia sicura della data
    const safeDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      0, 0, 0, 0
    );
    
    days.push({
      date: safeDate,
      dayString,
      isCurrentMonth,
      isToday,
      displayDay: dayDay
    });
    
    // Incrementa la data per il prossimo giorno
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  console.log('📅 Calendario generato:', {
    totalDays: days.length,
    month,
    firstDay: days[0],
    lastDay: days[days.length - 1],
    currentMonthDays: days.filter(d => d.isCurrentMonth).length
  });
  
  return days;
};

/**
 * Genera i giorni per la vista settimanale (solo Lun-Ven)
 */
export const generateWeekDays = (year: number, month: number): SafeDateInfo[] => {
  console.log('📅 generateWeekDays chiamata con:', { year, month });
  
  // Crea la prima settimana del mese selezionato
  const firstDayOfMonth = new Date(year, month, 1, 0, 0, 0, 0);
  
  // Trova il lunedì della prima settimana
  const firstDayWeekday = firstDayOfMonth.getDay();
  const daysToSubtract = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;
  
  const currentWeekStart = new Date(year, month, 1 - daysToSubtract, 0, 0, 0, 0);
  
  const weekDays: SafeDateInfo[] = [];
  for (let i = 0; i < 5; i++) { // Solo 5 giorni (Lun-Ven)
    const currentDate = new Date(currentWeekStart);
    currentDate.setDate(currentWeekStart.getDate() + i);
    
    const dayYear = currentDate.getFullYear();
    const dayMonth = currentDate.getMonth() + 1;
    const dayDay = currentDate.getDate();
    
    const dayString = `${dayYear}-${String(dayMonth).padStart(2, '0')}-${String(dayDay).padStart(2, '0')}`;
    const isCurrentMonth = currentDate.getMonth() === month;
    
    // Controlla se è oggi
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isToday = dayString === todayString;
    
    // Crea una copia sicura della data
    const safeDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      0, 0, 0, 0
    );
    
    weekDays.push({
      date: safeDate,
      dayString,
      isCurrentMonth,
      isToday,
      displayDay: dayDay
    });
  }
  
  return weekDays;
};

/**
 * Crea una chiave sicura per raggruppare i contenuti per data
 */
export const createDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Crea una data sicura per l'assegnazione da una data del calendario
 */
export const createAssignmentDate = (calendarDate: Date): Date => {
  return new Date(
    calendarDate.getFullYear(),
    calendarDate.getMonth(),
    calendarDate.getDate(),
    0, 0, 0, 0
  );
};